// ─────────────────────────────────────────────────────────────────────────────
// LOCAL COPY of @parsina/framework v0.23.0 `ui/lib/brand.js` — the code below the
// next rule is that file, unchanged.
//
// This panel pins the framework at v0.21.0, which has no brand module. Bumping it
// would carry two minor lines of kit changes (v0.22.x: currency, pickers, the
// clinical module) into an ops panel to get one ~450-line file, so the plan's
// rule for dentist/admin/student applies (docs/rebranding/PLAN.md D10, §7): copy
// it, and the day this panel moves to v0.23 or later, delete this file and point
// every import of '../lib/brand' at '@parsina/framework/ui/lib/brand'. Never
// edit the copy on its own: brand.test.js holds it to the same behaviour, and
// the pre-paint script in index.html mirrors its sanitising.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// The brand — what the product is called, what its credit is called, which
// languages it offers, which calendar it reads and which market it serves — and
// how a deployment replaces it.
//
// THE PROBLEM THIS SOLVES
// One set of images runs as Parsina and as another brand (Proxia first). The
// brand is CONFIGURATION, set by the deployment (docs/rebranding/CONTRACT.md):
// the container writes `BRAND_B64` into env.js, the app decodes it at boot and
// hands it here. Everything that says the name — the catalogues' `{{brand}}`,
// the printed prescription's footer, the video call's window title — and
// everything that is Iranian by default rather than by nature — the calendar,
// the phone field's +98, the national-id rule — asks this module.
//
// WHY A REGISTRY
// The same shape as `setUiStrings` (uiStrings.js) and `configureClinical`
// (clinical/src/host.jsx): module state, set once at boot, readable from plain
// functions — a print builder, a phone parser, a locale resolver — and not only
// from components. It is not reactive: a brand does not change while a page is
// open, so it is configured BEFORE the first render and never again.
//
// THE DEFAULTS ARE THE POINT
// Every default below is the literal the framework shipped before it had a
// brand. An app that never calls `configureBrand` renders exactly what it
// rendered before, byte for byte (clinical/__tests__/brandParity.test.js holds
// the resolved catalogues to that). A deployment that configures a brand
// replaces only the fields it sets; a bad value is dropped with ONE console
// warning and its default kept — a brand must never be the reason a panel does
// not start.
//
// Brand is not market (CONTRACT §1.4). `market: 'intl'` does not rename the
// Iranian rails; it hides them (`isIranMarket()`), at the entry points.
// ─────────────────────────────────────────────────────────────────────────────

const deepFreeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

// i18n-exempt: brand data, not UI copy — each value is the name the product (or
// its credit, or its tagline) has in that language, exactly as the catalogues
// spelled it before they said `{{brand}}`.
/**
 * Parsina — what every field means when the deployment leaves it unset.
 *
 * `name.ar` and `walletUnit.ar` are the framework's OWN Arabic spellings
 * (`nav.brand.name`, `common.units.coin`), not a normalised one: the Arabic
 * catalogue also spells the name «بارسينا», «بارسینا» and «پارسینا» in places, and
 * those sites keep their spelling through an inline default (`{{brand|بارسينا}}`,
 * see `brandToken`) until the Arabic copy is reviewed (PLAN D3).
 */
export const BRAND_DEFAULTS = deepFreeze({
  id: 'parsina',
  name: { fa: 'پارسینا', en: 'Parsina', ar: 'Parsina' },
  tagline: { fa: 'دستیار هوشمند پزشکی', en: 'Intelligent Medical Assistant', ar: 'المساعد الطبي الذكي' },
  // The credit's NAME, a label only — `parseh*` identifiers never change.
  // `faEz` is the Persian ezafe form («پارسهٔ کلینیک»): heh + U+0654.
  walletUnit: { fa: 'پارسه', faEz: 'پارسهٔ', en: 'Parseh', ar: 'بارسه' },
  // null = the app's own list / default / calendar / market — today's behaviour.
  locales: null,
  defaultLocale: null,
  calendar: null,
  market: null,
  assetPack: null,
  // Per-locale overlay, consulted BEFORE the catalogue: { en: { '<key>': '<text>' } }.
  strings: {},
});

const CALENDARS = ['jalali', 'gregory'];
const MARKETS = ['ir', 'intl'];
const SLUG = /^[a-z0-9][a-z0-9-]*$/;
const LOCALE_ID = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/;

let configured = deepFreeze({});   // what the deployment set, after sanitising
let resolved = BRAND_DEFAULTS;     // the defaults with `configured` merged over them
let revision = 0;

// One warning per message per session — `brandLocales` runs on every render.
const warned = new Set();
function warnOnce(message) {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(`[brand] ${message}`);
}

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const nonEmpty = (v) => typeof v === 'string' && v.trim() !== '';

/** `{ fa: '…', en: '…' }` — keeps the entries whose value is a non-empty string. */
function sanitizeMap(value, field, problems) {
  if (!isPlainObject(value)) { problems.push(`${field} is not an object`); return undefined; }
  const out = {};
  for (const [key, text] of Object.entries(value)) {
    if (nonEmpty(text)) out[key] = text;
    else problems.push(`${field}.${key} is not a non-empty string`);
  }
  return out;
}

function sanitizeLocales(value, problems) {
  if (!Array.isArray(value) || value.length === 0) {
    problems.push('locales is not a non-empty array');
    return undefined;
  }
  const ids = value.map((id) => (typeof id === 'string' ? id.trim().toLowerCase() : ''));
  if (ids.some((id) => !LOCALE_ID.test(id))) {
    problems.push(`locales has an entry that is not a language tag: ${JSON.stringify(value)}`);
    return undefined;
  }
  return [...new Set(ids)];
}

function sanitizeStrings(value, problems) {
  if (!isPlainObject(value)) { problems.push('strings is not an object'); return undefined; }
  const out = {};
  for (const [locale, table] of Object.entries(value)) {
    if (!isPlainObject(table)) { problems.push(`strings.${locale} is not an object`); continue; }
    const kept = {};
    for (const [key, text] of Object.entries(table)) {
      const ok = typeof text === 'string'
        || (Array.isArray(text) && text.every((item) => typeof item === 'string'));
      if (ok) kept[key] = text;
      else problems.push(`strings.${locale}.${key} is not a string`);
    }
    out[locale.toLowerCase()] = kept;
  }
  return out;
}

/**
 * Raw brand JSON → only the fields that are valid, with every problem noted.
 * Unknown fields are ignored without a word: the same object reaches every
 * runtime (CONTRACT §2) and may carry fields only a service reads.
 */
function sanitize(input, problems) {
  if (!isPlainObject(input)) {
    problems.push('the brand is not a JSON object');
    return {};
  }
  const out = {};
  const scalar = (field, test, normalise = (v) => v) => {
    const v = input[field];
    if (v === undefined || v === null) return;
    if (test(v)) out[field] = normalise(v);
    else problems.push(`${field} ${JSON.stringify(v)} is not valid`);
  };
  scalar('id', (v) => typeof v === 'string' && SLUG.test(v));
  scalar('calendar', (v) => CALENDARS.includes(v));
  scalar('market', (v) => MARKETS.includes(v));
  scalar('assetPack', (v) => typeof v === 'string' && SLUG.test(v));
  scalar('defaultLocale', (v) => typeof v === 'string' && LOCALE_ID.test(v.trim().toLowerCase()),
    (v) => v.trim().toLowerCase());
  for (const field of ['name', 'tagline', 'walletUnit']) {
    if (input[field] == null) continue;
    const map = sanitizeMap(input[field], field, problems);
    if (map && Object.keys(map).length) out[field] = map;
  }
  if (input.locales != null) {
    const locales = sanitizeLocales(input.locales, problems);
    if (locales) out.locales = locales;
  }
  if (input.strings != null) {
    const strings = sanitizeStrings(input.strings, problems);
    if (strings) out.strings = strings;
  }
  // A first-visit language the brand does not offer is a contradiction; the
  // list wins, and `brandDefaultLocale` then answers from the list.
  if (out.defaultLocale && out.locales && !out.locales.includes(out.defaultLocale)) {
    problems.push(`defaultLocale "${out.defaultLocale}" is not one of locales`);
    delete out.defaultLocale;
  }
  return out;
}

/**
 * The Persian ezafe of a credit name, for a brand that names its credit in
 * Persian and does not spell `faEz` itself: «پارسه» → «پارسهٔ», «دارو» → «داروی».
 * A consonant, a «ی» or a Latin word carries an unwritten kasra and is
 * returned as it is.
 */
export function persianEzafe(word) {
  const w = String(word ?? '');
  if (/ه$/.test(w)) return `${w}ٔ`;
  if (/[او]$/.test(w)) return `${w}ی`;
  return w;
}

function merge(clean) {
  const unit = clean.walletUnit || {};
  return deepFreeze({
    ...BRAND_DEFAULTS,
    ...clean,
    name: { ...BRAND_DEFAULTS.name, ...clean.name },
    tagline: { ...BRAND_DEFAULTS.tagline, ...clean.tagline },
    walletUnit: {
      ...BRAND_DEFAULTS.walletUnit,
      ...unit,
      faEz: unit.faEz ?? (unit.fa ? persianEzafe(unit.fa) : BRAND_DEFAULTS.walletUnit.faEz),
    },
    strings: clean.strings || {},
  });
}

/**
 * Configure the brand. Call once at boot, before the first render.
 *
 * Merges `partial` onto the DEFAULTS (not onto an earlier call), so calling it
 * twice with the same object is the same as calling it once, and
 * `configureBrand(null)` restores Parsina — which is what a test teardown wants.
 * A field that is not valid is dropped and its default kept; all of a call's
 * problems are reported in ONE `console.warn`.
 *
 * @param {object|null} partial  the brand object (CONTRACT §2); any subset
 * @returns {object} the brand now in force, as `brand()` returns it
 */
export function configureBrand(partial) {
  revision += 1;
  if (partial === null || partial === undefined) {
    configured = deepFreeze({});
    resolved = BRAND_DEFAULTS;
    return resolved;
  }
  const problems = [];
  const clean = sanitize(partial, problems);
  if (problems.length) {
    console.warn(`[brand] kept the default for what is not valid: ${problems.join('; ')}`);
  }
  configured = deepFreeze(clean);
  resolved = Object.keys(clean).length ? merge(clean) : BRAND_DEFAULTS;
  return resolved;
}

/**
 * `BRAND_B64` (base64 of the UTF-8 JSON, CONTRACT §3) → the brand object, or
 * undefined when it is not one. Never throws. Whitespace is ignored: `base64`
 * without `-w0` wraps its output every 76 characters.
 */
export function decodeBrandB64(b64) {
  try {
    if (typeof atob !== 'function') return undefined;
    const binary = atob(String(b64).replace(/\s+/g, ''));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    return undefined;
  }
}

/**
 * Configure the brand from the runtime env an SPA's env.js wrote — by default
 * `window.__ENV__`. The entrypoint writes `BRAND_B64` only when the container
 * has a brand, so its absence is normal and changes nothing; a value that does
 * not decode is a bad brand: Parsina, and one warning.
 *
 * @param {object} [env]  `{ BRAND_B64 }`; defaults to `window.__ENV__`
 * @returns {object} the brand now in force
 */
export function configureBrandFromEnv(env = (typeof window !== 'undefined' ? window.__ENV__ : undefined)) {
  const b64 = env?.BRAND_B64;
  if (b64 === undefined || b64 === null || String(b64).trim() === '') return resolved;
  const decoded = decodeBrandB64(b64);
  if (decoded === undefined) {
    console.warn('[brand] BRAND_B64 is not base64 of a UTF-8 JSON object — Parsina defaults kept');
    return configureBrand(null);
  }
  return configureBrand(decoded);
}

/** The brand in force: the defaults with the configured fields merged over them. Frozen. */
export function brand() {
  return resolved;
}

/**
 * Bumped by every `configureBrand`. For a cache that derives from the brand
 * (a translator built outside a provider) to know it is stale.
 */
export function brandRevision() {
  return revision;
}

/** False only for a brand that says `market: 'intl'`. Gate Iran-only surfaces on it. */
export function isIranMarket() {
  return resolved.market !== 'intl';
}

/**
 * The languages to OFFER, out of the ones an app ships: `appLocales` filtered to
 * the brand's `locales`, in the app's order. Without a brand list it is
 * `appLocales` itself. A brand list that shares nothing with the app's is a
 * misconfiguration and is ignored (with a warning) rather than offering nothing.
 *
 * @param {string[]} appLocales  the ids the app ships, e.g. ['fa', 'en', 'ar']
 * @returns {string[]}
 */
export function brandLocales(appLocales) {
  const list = Array.isArray(appLocales) ? appLocales : [];
  const allowed = resolved.locales;
  if (!allowed) return list;
  const offered = list.filter((id) => allowed.includes(String(id).toLowerCase()));
  if (offered.length) return offered;
  warnOnce(`locales ${JSON.stringify(allowed)} share nothing with this app's ${JSON.stringify(list)} — offering the app's`);
  return list;
}

/**
 * The language for a first visit — someone who has chosen nothing and whose
 * browser named nothing the app offers. The brand's `defaultLocale` when it is
 * offered; else `appDefault` when that is offered; else the first offered one.
 * Without a brand it is `appDefault`.
 *
 * @param {string}   appDefault    the app's own first-visit language
 * @param {string[]} [appLocales]  the ids the app ships; narrows the answer to them
 */
export function brandDefaultLocale(appDefault, appLocales) {
  const offered = appLocales ? brandLocales(appLocales) : resolved.locales;
  const wanted = resolved.defaultLocale;
  if (wanted && (!offered || offered.includes(wanted))) return wanted;
  if (!offered || offered.includes(appDefault)) return appDefault;
  return offered[0] ?? appDefault;
}

/**
 * The calendar dates are read in: the brand's `calendar` ('jalali' | 'gregory')
 * when it sets one, else `appDefault`. Pass null to ask "does the brand decide?".
 */
export function calendarSystem(appDefault = null) {
  return resolved.calendar || appDefault;
}

// ── Text ─────────────────────────────────────────────────────────────────────

/** A per-locale map read for `locale`: exact, then the base language, then English. */
function pick(map, locale) {
  if (!map) return undefined;
  const id = String(locale || '').toLowerCase();
  if (map[id] !== undefined) return map[id];
  const base = id.split(/[-_]/)[0];
  if (map[base] !== undefined) return map[base];
  return map.en;
}

/** What the deployment itself set for `field` in `locale`, or undefined. */
const configuredIn = (field, locale) => {
  const map = configured[field];
  if (!map) return undefined;
  const id = String(locale || '').toLowerCase();
  return map[id] ?? map[id.split(/[-_]/)[0]];
};

/** The product's name in `locale` — «پارسینا», "Parsina", or the configured brand's. */
export function brandName(locale) {
  return pick(resolved.name, locale);
}

/**
 * One catalogue token's value in `locale`, or undefined for a name that is not a
 * brand token (so a caller can leave it to its own interpolation).
 *
 *   {{brand}}    the product name          name[locale]
 *   {{coin}}     the credit's name         walletUnit[locale]
 *   {{coinEz}}   its Persian ezafe form    walletUnit.faEz
 *   {{tagline}}  the product's tagline     tagline[locale]
 *
 * `inline` is the literal after a `|` — `{{brand|بارسينا}}`. It is what the site
 * said before it had a token, used for as long as the deployment leaves that
 * value unset: a catalogue that spells the brand two ways in one language keeps
 * both spellings for Parsina, and reads the configured name for any other brand.
 */
export function brandToken(name, locale, inline) {
  const has = inline !== undefined && inline !== null;
  switch (name) {
    case 'brand': {
      const set = configuredIn('name', locale);
      return set ?? (has ? inline : pick(resolved.name, locale));
    }
    case 'coin': {
      const set = configuredIn('walletUnit', locale);
      return set ?? (has ? inline : pick(resolved.walletUnit, locale));
    }
    case 'coinEz': {
      const unit = configured.walletUnit;
      const set = unit && (unit.faEz ?? (unit.fa ? persianEzafe(unit.fa) : undefined));
      return set ?? (has ? inline : resolved.walletUnit.faEz);
    }
    case 'tagline': {
      const set = configuredIn('tagline', locale);
      return set ?? (has ? inline : pick(resolved.tagline, locale));
    }
    default:
      return undefined;
  }
}

/**
 * The catalogue tokens that resolve themselves. A placeholder-parity check
 * ("every locale interpolates the same `{{names}}`") leaves these out: they are
 * never a hole a caller must fill, and Persian legitimately says `{{coinEz}}`
 * where English says `{{coin}}`, or names the credit twice where English names
 * it once.
 */
export const BRAND_TOKENS = Object.freeze(['brand', 'coin', 'coinEz', 'tagline']);

/** The four token values for `locale`, for a translator's default-values seam. */
export function brandTokens(locale) {
  return {
    brand: brandToken('brand', locale),
    coin: brandToken('coin', locale),
    coinEz: brandToken('coinEz', locale),
    tagline: brandToken('tagline', locale),
  };
}

/**
 * A catalogue placeholder: `{{name}}` or `{{name|inline default}}`. Shared with
 * the clinical translator so both read the same syntax.
 */
export const PLACEHOLDER = /\{\{\s*([\w.]+)\s*(?:\|([^{}]*?))?\s*\}\}/g;

/**
 * Resolve the brand tokens in `text` for `locale`; every other placeholder is
 * left exactly as written for the caller's own interpolation. For an app with
 * its own translator: run the looked-up string through this, then interpolate.
 */
export function applyBrandTokens(text, locale) {
  if (typeof text !== 'string' || text.indexOf('{{') === -1) return text;
  return text.replace(PLACEHOLDER, (match, name, inline) => {
    const value = brandToken(name, locale, inline);
    return value === undefined ? match : value;
  });
}

/**
 * The brand's own text for catalogue `key` in `locale` (`strings` overlay), or
 * undefined. A translator asks this BEFORE its catalogue.
 */
export function brandString(locale, key) {
  const table = resolved.strings;
  const id = String(locale || '').toLowerCase();
  const own = table[id] ?? table[id.split(/[-_]/)[0]];
  if (!own) return undefined;
  return Object.prototype.hasOwnProperty.call(own, key) ? own[key] : undefined;
}
